import React, { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { usePermissions } from "@/hooks/usePermissions";
import { getContextActions } from "@/lib/contextMenu/actions";
import type { EntityContextType, ContextAction } from "@/lib/contextMenu/types";

interface EntityContextMenuProps {
  entityType: EntityContextType;
  entityId: string;
  /** Row data for copy/print actions */
  data: any;
  /** Handler for "custom" actions — receives the action ID and row data */
  onAction?: (actionId: string, data: any) => void;
  children: React.ReactNode;
}

function getNestedValue(obj: any, path: string): any {
  return path.split(".").reduce((o, k) => o?.[k], obj);
}

export const EntityContextMenu = ({
  entityType,
  entityId,
  data,
  onAction,
  children,
}: EntityContextMenuProps) => {
  const navigate = useNavigate();
  const { can, isAdmin, isCustomer, isVendor, isPersonnel, isContractLabor } =
    usePermissions();

  const roleMap: Record<string, boolean> = {
    customer: isCustomer,
    vendor: isVendor,
    personnel: isPersonnel,
    contract_labor: isContractLabor,
  };

  const actions = getContextActions(entityType);

  const filteredActions = actions.filter((action) => {
    // Role-based visibility
    if (action.hideForRoles?.some((role) => roleMap[role])) return false;
    // Permission check
    if (action.permission && !isAdmin && !can(action.permission)) return false;
    return true;
  });

  const handleAction = useCallback(
    (action: ContextAction) => {
      switch (action.handler) {
        case "navigate":
          if (action.path) navigate(action.path(entityId));
          break;
        case "navigate_new_tab":
          if (action.path) window.open(action.path(entityId), "_blank");
          break;
        case "copy":
          if (action.copyField) {
            const val = getNestedValue(data, action.copyField);
            if (val) {
              navigator.clipboard.writeText(String(val));
              toast.success(`Copied "${val}"`);
            } else {
              toast.error("Nothing to copy");
            }
          }
          break;
        case "print":
          onAction?.("print", data);
          break;
        case "custom":
          onAction?.(action.id, data);
          break;
      }
    },
    [entityId, data, navigate, onAction]
  );

  if (filteredActions.length === 0) {
    return <>{children}</>;
  }

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
      <ContextMenuContent className="w-52">
        {filteredActions.map((action, i) => (
          <React.Fragment key={action.id}>
            {action.divider && i > 0 && <ContextMenuSeparator />}
            <ContextMenuItem
              onClick={() => handleAction(action)}
              className={`gap-2 ${
                action.destructive
                  ? "text-destructive focus:text-destructive"
                  : ""
              }`}
            >
              <action.icon className="h-4 w-4" />
              {action.label}
            </ContextMenuItem>
          </React.Fragment>
        ))}
      </ContextMenuContent>
    </ContextMenu>
  );
};
